import type { EstateImage } from '@acasa/shared'
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

/**
 * onOffice's image CDN resizes on demand: appending `@<width>x<height>` to the
 * stored URL returns that variant, and asking for more than the original simply
 * returns the original.
 *
 * This is why the mirror stores the bare URL. A thumbnail is ~19 kB against
 * ~234 kB for the full file, and a strip of twenty-five originals is several
 * megabytes for pictures displayed at 96px wide.
 */
export function sizedImageUrl(image: EstateImage, width: number, height: number) {
  return `${image.url}@${width}x${height}`
}

/** Only categories that are not ordinary photos are worth labelling. */
function badgeFor(category: string) {
  return category === 'Foto' || category === 'Foto_gross' ? null : category
}

function Thumbnail({ image, onOpen }: { image: EstateImage; onOpen: () => void }) {
  const badge = badgeFor(image.category)

  return (
    <button
      type="button"
      onClick={onOpen}
      title={image.title ?? image.category}
      className="border-line hover:border-brand focus-visible:border-brand group relative h-16 w-24 shrink-0 overflow-hidden border transition-colors"
    >
      <img
        // 2x the rendered box, so the strip stays sharp on a retina screen.
        src={sizedImageUrl(image, 200, 150)}
        alt={image.title ?? image.category}
        loading="lazy"
        // The CDN does not need to know which internal page is looking at it.
        referrerPolicy="no-referrer"
        className="bg-raised h-full w-full object-cover"
      />
      {badge && (
        <span className="bg-canvas/85 text-muted absolute bottom-0 left-0 px-1 py-0.5 text-[9px] tracking-wide uppercase">
          {badge}
        </span>
      )}
    </button>
  )
}

/**
 * Full-size viewer.
 *
 * Deliberately dependency-free, in the same spirit as `components/ui/popover`:
 * Escape closes, arrow keys step, a click on the backdrop closes. The image is
 * requested at 1600px, which the CDN caps at the original.
 */
function Lightbox({
  images,
  index,
  onClose,
  onStep,
}: {
  images: EstateImage[]
  index: number
  onClose: () => void
  onStep: (delta: number) => void
}) {
  const image = images[index]

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
      if (event.key === 'ArrowRight') onStep(1)
      if (event.key === 'ArrowLeft') onStep(-1)
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose, onStep])

  if (!image) return null

  return (
    // Escape and the arrow keys are bound on `document` in the effect above,
    // which is what makes this operable without a pointer; a key handler on the
    // backdrop itself would only fire once focus was already inside it.
    // biome-ignore lint/a11y/useKeyWithClickEvents: see above
    <div
      role="dialog"
      aria-modal="true"
      aria-label={image.title ?? image.category}
      // Not `bg-canvas`: a viewer wants the surrounding UI out of the way, and
      // a neutral scrim reads the same in both themes.
      className="fixed inset-0 z-50 flex flex-col bg-black/80 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div className="flex items-center justify-between gap-4 text-white">
        <span className="min-w-0 text-xs">
          <span className="truncate font-medium">{image.title || image.category}</span>
          <span className="ml-2 opacity-70">
            {index + 1} / {images.length} · {image.category}
          </span>
        </span>
        <button
          type="button"
          onClick={onClose}
          // Focus has to move INTO the dialog, or the first Tab lands back on the
          // page behind it and Escape goes to the wrong handler.
          // biome-ignore lint/a11y/noAutofocus: see above
          autoFocus
          className="shrink-0 border border-white/30 px-3 py-1.5 text-xs transition-colors hover:border-white"
        >
          Schließen
        </button>
      </div>

      {/* Stop propagation so clicking the picture itself does not close it. */}
      {/* Not an interaction: this handler only stops the backdrop's, so there is
          no behaviour here for a keyboard to reach. */}
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: see above */}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: see above */}
      <div
        className="flex min-h-0 flex-1 items-center justify-center gap-3 pt-4"
        onClick={(event) => event.stopPropagation()}
      >
        {images.length > 1 && (
          <button
            type="button"
            onClick={() => onStep(-1)}
            aria-label="Vorheriges Bild"
            className="shrink-0 px-2 py-6 text-2xl text-white/60 transition-colors hover:text-white"
          >
            ‹
          </button>
        )}

        <img
          src={sizedImageUrl(image, 1600, 1200)}
          alt={image.title ?? image.category}
          referrerPolicy="no-referrer"
          className="max-h-full min-h-0 max-w-full object-contain"
        />

        {images.length > 1 && (
          <button
            type="button"
            onClick={() => onStep(1)}
            aria-label="Nächstes Bild"
            className="shrink-0 px-2 py-6 text-2xl text-white/60 transition-colors hover:text-white"
          >
            ›
          </button>
        )}
      </div>
    </div>
  )
}

export function EstateImageStrip({
  images,
  estateId,
}: {
  images: EstateImage[]
  estateId: number
}) {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  // Wraps in both directions, so arrowing past the end is never a dead end.
  const step = useCallback(
    (delta: number) =>
      setOpenIndex((current) =>
        current === null ? current : (current + delta + images.length) % images.length,
      ),
    [images.length],
  )

  const close = useCallback(() => setOpenIndex(null), [])

  if (images.length === 0) return null

  return (
    <div className="border-line mt-4 border-t pt-3">
      <p className="text-faint mb-1.5 flex items-baseline justify-between gap-2 text-[10px] tracking-wider uppercase">
        <span>Bilder · {images.length}</span>
        <Link
          to={`/bild-ki?estate=${estateId}`}
          className="text-muted hover:text-brand normal-case underline underline-offset-2"
        >
          Mit KI bearbeiten
        </Link>
      </p>

      <div className="-mx-0.5 flex gap-1.5 overflow-x-auto px-0.5 pb-1">
        {images.map((image, index) => (
          <Thumbnail key={image.id} image={image} onOpen={() => setOpenIndex(index)} />
        ))}
      </div>

      {openIndex !== null && (
        <Lightbox images={images} index={openIndex} onClose={close} onStep={step} />
      )}
    </div>
  )
}
